import { Controller } from "@hotwired/stimulus";

/**
 * Cart "Quote" sharing controller (identifier: "share-cart", auto-mapped from filename).
 *
 * Backs the cart's Download-PDF / Copy-link / Send-email UI.
 */
export default class extends Controller {
  static values = { createUrl: String };
  static targets = ["share", "shareButton", "shareUrl", "emailInput", "status"];

  // Shared fetch options. The CSRF token is included only when the meta tag is
  // present (it may be absent on some layouts), so a missing tag never throws.
  jsonHeaders() {
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };
    const csrf = document.querySelector('meta[name="csrf-token"]');
    if (csrf) headers["X-CSRF-Token"] = csrf.getAttribute("content");
    return headers;
  }

  // Creates (or reuses) the Quote snapshot for the current cart.
  // Response shape: { access_key, quote_number, pdf_url, share_url }.
  async createQuote() {
    const response = await fetch(this.createUrlValue, {
      method: "POST",
      headers: this.jsonHeaders(),
    });
    if (!response.ok)
      throw new Error(`Create quote failed: ${response.status}`);
    return response.json();
  }

  // Download-PDF entry point: snapshot the cart, then open the PDF in a new tab.
  async downloadPdf() {
    try {
      const data = await this.createQuote();
      window.open(data.pdf_url, "_blank");
    } catch (error) {
      console.error(error);
    }
  }

  // Share entry point: snapshot the cart, populate the readonly share URL input,
  // remember the access key (needed for the email endpoint), then reveal the
  // inline share section.
  async openShare() {
    try {
      const data = await this.createQuote();
      this.shareUrlTarget.value = data.share_url;
      this.accessKey = data.access_key;
      // Reveal every share section (share-link + send-email).
      this.shareTargets.forEach((section) => (section.hidden = false));
      // Once expanded, the Share trigger is redundant. Use inline display:none
      // because `.button-group > *` sets display:inline-block, which overrides
      // the [hidden] attribute.
      this.shareButtonTarget.style.display = "none";
    } catch (error) {
      console.error(error);
    }
  }

  // Send-email entry point: emails the share link to the entered recipient.
  // Response: { ok: true } or { ok: false, error } (HTTP 422).
  async sendEmail() {
    try {
      const response = await fetch(`/quotes/${this.accessKey}/share_email`, {
        method: "POST",
        headers: this.jsonHeaders(),
        body: JSON.stringify({ email: this.emailInputTarget.value }),
      });
      const data = await response.json();
      if (response.ok && data.ok) {
        this.statusTarget.textContent = "Email sent.";
      } else {
        this.statusTarget.textContent = data.error || "Could not send email.";
      }
    } catch (error) {
      console.error(error);
      this.statusTarget.textContent = "Could not send email.";
    }
  }

}
